import { PDFDocument } from "pdf-lib";
import { renderAssemblySheet } from "./assembly-sheet.js";
import { MM_TO_PT, paperDimsMm } from "./paper.js";
import { renderRequestToPngs } from "./to-png.js";
import type { ExportRequest, PageRenderer } from "./types.js";

/**
 * Render an {@link ExportRequest} to a multi-page PDF using pdf-lib.
 *
 * Each page is rendered to a PNG bitmap via the surface-specific renderer,
 * then embedded into a PDF page that has the paper's exact dimensions in
 * points (1 mm = 72 / 25.4 pt). Image fits the entire page (no margins).
 */
export async function exportRequestToPdf<TSnap>(
  snapshot: TSnap,
  request: ExportRequest,
  renderer: PageRenderer<TSnap>,
): Promise<Uint8Array> {
  const rendered = await renderRequestToPngs(snapshot, request, renderer);

  const doc = await PDFDocument.create();
  for (const r of rendered) {
    const { wMm, hMm } = paperDimsMm(r.page.paper, r.page.orientation);
    const pageWidthPt = wMm * MM_TO_PT;
    const pageHeightPt = hMm * MM_TO_PT;
    const png = await doc.embedPng(r.pngBytes);
    const pdfPage = doc.addPage([pageWidthPt, pageHeightPt]);
    pdfPage.drawImage(png, {
      x: 0,
      y: 0,
      width: pageWidthPt,
      height: pageHeightPt,
    });
  }

  // Multi-sheet posters get an assembly guide as the final page. It is drawn
  // on the same sheet the poster prints on, so the whole PDF stays one format.
  const first = rendered[0];
  if (request.assembly && first) {
    const guide = await renderAssemblySheet({
      info: request.assembly,
      paper: first.page.paper,
      orientation: first.page.orientation,
      pixelRatio: request.pixelRatio ?? 2,
    });
    const { wMm, hMm } = paperDimsMm(first.page.paper, first.page.orientation);
    const png = await doc.embedPng(guide.pngBytes);
    const pdfPage = doc.addPage([wMm * MM_TO_PT, hMm * MM_TO_PT]);
    pdfPage.drawImage(png, {
      x: 0,
      y: 0,
      width: wMm * MM_TO_PT,
      height: hMm * MM_TO_PT,
    });
  }

  return doc.save();
}

/** Trigger a browser download for raw bytes. */
export function downloadBytes(bytes: Uint8Array, filename: string, mime: string) {
  // Copy into a fresh ArrayBuffer-backed Uint8Array so BlobPart is happy
  // even when `bytes.buffer` is a SharedArrayBuffer (sql.js etc.).
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
